import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';

/** Attaches the session token and turns auth/permission failures into something usable. */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const toasts = inject(ToastService);

  // Renew on activity: an operator filling in a long form is exactly the person a lapsing
  // token would strand. Skipped for the renewal call itself, which would otherwise recurse,
  // and for sign-in, which has no session to renew.
  const isAuthCall = req.url.endsWith('/auth/refresh') || req.url.endsWith('/auth/login');
  const renewal = !isAuthCall && auth.needsRefresh() ? from(auth.refresh()) : null;

  const send = () => {
    const token = auth.token;
    const authorised = token
      ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : req;

    return next(authorised);
  };

  return (renewal ? renewal.pipe(switchMap(send)) : send()).pipe(
    catchError((error: HttpErrorResponse) => {
      const isLoginAttempt = req.url.endsWith('/auth/login');

      if (error.status === 401 && !isLoginAttempt) {
        auth.clear();
        void router.navigate(['/login']);
      } else if (error.status === 403) {
        // Server-side authorization refused. Surfacing it beats a silently empty screen.
        toasts.error('لا تملك صلاحية تنفيذ هذا الإجراء.');
      } else if (error.status === 0) {
        toasts.error('تعذّر الوصول إلى الخادم. تحقق من الاتصال ثم أعد المحاولة.');
      }

      return throwError(() => error);
    }),
  );
};

/** Pulls the server's `{ error }` message out of a failed response. */
export function apiMessage(error: unknown, fallback: string): string {
  if (error instanceof HttpErrorResponse) {
    const body = error.error as { error?: string } | string | null;
    if (typeof body === 'string' && body.trim()) {
      return body;
    }
    if (body && typeof body === 'object' && body.error) {
      return body.error;
    }
  }
  return fallback;
}
